import React, {useContext, useState} from "react";
import {TextField, MenuItem, Button, Select, FormControl, InputLabel, Box} from "@mui/material";
import {DatePicker, TimePicker} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import * as yup from "yup";
import {useFormik} from "formik";
import Typography from "@mui/material/Typography";
import EmailBadge from "../NewStudio/EmailBadge";
import {LibrettoStudioTheme, RefreshTokenAndRetry} from "../../utils/utils";
import {FetchContext} from "../../context/FetchContext";
import {AuthContext} from "../../context/AuthContext";
import {SessionTrashButton} from "../../components/CoolButtons";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {StudioBackIconButton} from "../NewStudio/SettingsIcon";

dayjs.extend(utc);
dayjs.extend(timezone);

const parseTime = (time) => {
    const [hourMinute, period] = time.split(" ");
    let [hour, minute] = hourMinute.split(":").map(Number);

    // Convert to 24-hour format
    if (period === "PM" && hour !== 12) hour += 12;
    if (period === "AM" && hour === 12) hour = 0;

    return hour * 60 + minute; // Return total minutes since midnight
};

const getDuration = (a, b) => {
    const minutesA = parseTime(a);
    const minutesB = parseTime(b);
    const difference = Math.abs(minutesB - minutesA); // Duration in minutes

    if (difference % 60 === 0) {
        return `${difference / 60} hr`;
    } else if (difference < 60) {
        return `${difference} min`;
    } else {
        const hours = Math.floor(difference / 60);
        const minutes = difference % 60;
        return `${hours} hr ${minutes} min`;
    }
};

const superHeaderText = {
    fontFamily: "Inter",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: "650",
    lineHeight: "24px",
    letterSpacing: "0.18px",
}

const topTitleStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    alignSelf: 'stretch',
}

const ScheduleSessionForm = ({onClose, projectId}) => {
    const defaultTimezone = React.useMemo(() => (
        (typeof Intl !== "undefined" && Intl.DateTimeFormat().resolvedOptions().timeZone) ||
        "America/New_York"
    ), []);

    const {nearestTime, oneHourLater} = React.useMemo(() => {
        const now = dayjs.tz(dayjs(), defaultTimezone);
        const roundedMinutes = Math.ceil(now.minute() / 15) * 15;
        const roundedTime = now.startOf("hour").minute(roundedMinutes);
        return {
            nearestTime: roundedTime.format("h:mm A"),
            oneHourLater: roundedTime.add(1, "hour").format("h:mm A"),
        };
    }, [defaultTimezone]);

    const parseTime = (time) => {
        const [hourMinute, period] = time.split(" ");
        let [hour, minute] = hourMinute.split(":").map(Number);
        if (period === "PM" && hour !== 12) hour += 12;
        if (period === "AM" && hour === 12) hour = 0;
        return hour * 60 + minute;
    };

    const getDuration = (a, b) => {
        const minutesA = parseTime(a);
        const minutesB = parseTime(b);
        const difference = Math.abs(minutesB - minutesA);
        if (difference % 60 === 0) {
            return `${difference / 60} hr`;
        } else if (difference < 60) {
            return `${difference} min`;
        } else {
            const hours = Math.floor(difference / 60);
            const minutes = difference % 60;
            return `${hours} hr ${minutes} min`;
        }
    };

    const [sessionName, setSessionName] = useState("");
    const [sessionNameTouched, setSessionNameTouched] = useState(false);
    const [sessionDescription, setSessionDescription] = useState("");
    const [date, setDate] = useState(dayjs());
    const [startTime, setStartTime] = useState(nearestTime);
    const [endTime, setEndTime] = useState(oneHourLater);
    const [selectedTimezone, setSelectedTimezone] = useState(defaultTimezone);

    const fetchContext = useContext(FetchContext);
    const authContext = useContext(AuthContext);

    const timezones = React.useMemo(() => [
        {label: "(GMT-11:00) Pacific/Midway", value: "Pacific/Midway"},
        {label: "(GMT-10:00) Pacific/Honolulu", value: "Pacific/Honolulu"},
        {label: "(GMT-08:00) America/Los_Angeles", value: "America/Los_Angeles"},
        {label: "(GMT-07:00) America/Denver", value: "America/Denver"},
        {label: "(GMT-06:00) America/Chicago", value: "America/Chicago"},
        {label: "(GMT-05:00) America/New_York", value: "America/New_York"},
        {label: "(GMT+00:00) Europe/London", value: "Europe/London"},
        {label: "(GMT+01:00) Europe/Paris", value: "Europe/Paris"},
        {label: "(GMT+05:30) Asia/Kolkata", value: "Asia/Kolkata"},
        {label: "(GMT+08:00) Asia/Shanghai", value: "Asia/Shanghai"},
        {label: "(GMT+09:00) Asia/Tokyo", value: "Asia/Tokyo"},
        {label: "(GMT+10:00) Australia/Sydney", value: "Australia/Sydney"},
    ], []);

    const generateTimes = React.useCallback(() => {
        const times = [];
        const periods = ["AM", "PM"];
        for (let period of periods) {
            for (let hour = 0; hour < 12; hour++) {
                for (let minute = 0; minute < 60; minute += 15) {
                    const formattedHour = hour === 0 ? 12 : hour;
                    const formattedMinute = minute.toString().padStart(2, "0");
                    times.push(`${formattedHour}:${formattedMinute} ${period}`);
                }
            }
        }
        return times;
    }, []);

    const times = React.useMemo(() => generateTimes(), [generateTimes]);

    const validationSchema = yup.object({
        email: yup.string().trim().email('Please enter a valid email address').required('Email is required.'),
    });

    const initialValues = {email: ''};
    const [addedEmails, setAddedEmails] = useState([]);
    const [emailError, setEmailError] = useState(false);
    const [emailAlreadyInvited, setEmailAlreadyInvited] = useState('');
    const formik = useFormik({initialValues, validationSchema});

    const handleCreateSession = async () => {
        try {
            const selectedTimezoneValue = selectedTimezone;
            const dateString = date.format("YYYY-MM-DD");
            const startTime24Hr = dayjs(startTime, ["h:mm A"]).format("HH:mm");
            const combinedStartTime = dayjs.tz(`${dateString} ${startTime24Hr}`, "YYYY-MM-DD HH:mm", selectedTimezoneValue);
            const endTime24Hr = dayjs(endTime, ["h:mm A"]).format("HH:mm");
            const combinedEndTime = dayjs.tz(`${dateString} ${endTime24Hr}`, "YYYY-MM-DD HH:mm", selectedTimezoneValue);
            const utcStartTimestamp = combinedStartTime.utc().unix();
            const utcEndTimestamp = combinedEndTime.utc().unix();

            await fetchContext.authAxios.post('/schedule', {
                startTimestamp: utcStartTimestamp,
                endTimestamp: utcEndTimestamp,
                sessionName: sessionName,
                description: sessionDescription,
                addedEmails: addedEmails,
                projectId: projectId,
                timezone: selectedTimezoneValue,
            }, {
                headers: {Authorization: `Bearer ${authContext.getToken()}`}
            });

            onClose();
        } catch (err) {
            if (err.response?.status === 401) {
                await RefreshTokenAndRetry(err, authContext, fetchContext);
            }
            console.error("Error creating session:", err);
        }
    };

    const handleAddEmail = () => {
        if (Boolean(formik.errors.email)) {
            setEmailError(true);
            setEmailAlreadyInvited('');
            return;
        }
        setEmailError(false);
        if (addedEmails.includes(formik.values.email)) {
            setEmailAlreadyInvited(formik.values.email);
            return;
        }
        setEmailAlreadyInvited('');
        setAddedEmails([...addedEmails, formik.values.email]);
    };

    const removeAddedEmail = (email) => {
        setAddedEmails(addedEmails.filter((addedEmail) => addedEmail !== email));
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{width: '100%', maxWidth: '715px', marginTop: "-40px"}}>
                <TextField
                    fullWidth
                    label="Session name"
                    variant="standard"
                    value={sessionName}
                    onChange={(e) => {
                        setSessionName(e.target.value);
                        setSessionNameTouched(true);
                    }}
                    error={!sessionName && sessionNameTouched}
                    helperText={<Box
                        height="1em">{!sessionName && sessionNameTouched ? 'Enter a session name' : ''}</Box>}
                    margin="normal"
                    InputProps={{
                        sx: {
                            height: '56px',
                            fontSize: '28px',
                            fontFamily: 'Inter',
                            color: '#000',
                            padding: '0 8px',
                            alignItems: 'center'
                        }, disableUnderline: false
                    }}
                    inputProps={{style: {fontSize: '28px', fontFamily: 'Inter', paddingBottom: '4px'}}}
                    InputLabelProps={{sx: {fontSize: '28px', fontFamily: 'Inter', color: '#555'}}}
                />

                <Box sx={{display: 'flex', justifyContent: 'space-between', gap: '10px', marginTop: '16px'}}>
                    <DatePicker
                        label="Date"
                        value={date}
                        minDate={dayjs.tz(dayjs(), selectedTimezone)}
                        onChange={(newDate) => setDate(newDate)}
                        renderInput={(params) => (
                            <TextField {...params} fullWidth margin="normal"
                                       InputProps={{style: {color: '#000', fontFamily: 'Inter'}}}
                                       InputLabelProps={{style: {color: '#555', fontFamily: 'Inter'}}}/>
                        )}
                    />

                    <FormControl sx={{width: '150px'}}>
                        <InputLabel sx={{color: '#555', fontFamily: 'Inter'}}>Start time</InputLabel>
                        <Select
                            value={startTime}
                            label={"Start time"}
                            onChange={(e) => {
                                setStartTime(e.target.value);
                                setEndTime(times[(times.indexOf(e.target.value) + 4) % times.length]);
                            }}
                            displayEmpty
                            sx={{color: '#000', fontFamily: 'Inter'}}
                        >
                            {times.filter((time) => {
                                const now = dayjs.tz(dayjs(), selectedTimezone);
                                const selectedDate = dayjs.tz(date.format('YYYY-MM-DD'), 'YYYY-MM-DD', selectedTimezone);
                                if (selectedDate.isSame(now, 'day')) {
                                    return dayjs.tz(`${date.format('YYYY-MM-DD')} ${time}`, 'YYYY-MM-DD h:mm A', selectedTimezone).isAfter(now);
                                }
                                return true;
                            }).map((time, index) => (
                                <MenuItem key={index} value={time}>{time}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl sx={{width: '150px'}}>
                        <InputLabel sx={{color: '#555', fontFamily: 'Inter'}}>End time</InputLabel>
                        <Select
                            value={endTime}
                            label={"End time"}
                            onChange={(e) => setEndTime(e.target.value)}
                            displayEmpty
                            sx={{color: '#000', fontFamily: 'Inter'}}
                        >
                            {times.filter((time) => {
                                const now = dayjs.tz(dayjs(), selectedTimezone);
                                const selectedDate = dayjs.tz(date.format('YYYY-MM-DD'), 'YYYY-MM-DD', selectedTimezone);
                                const selectedStartTime = dayjs.tz(`${date.format('YYYY-MM-DD')} ${startTime}`, 'YYYY-MM-DD h:mm A', selectedTimezone);
                                if (selectedDate.isSame(now, 'day')) {
                                    return dayjs.tz(`${date.format('YYYY-MM-DD')} ${time}`, 'YYYY-MM-DD h:mm A', selectedTimezone).isAfter(selectedStartTime) &&
                                        dayjs.tz(`${date.format('YYYY-MM-DD')} ${time}`, 'YYYY-MM-DD h:mm A', selectedTimezone).isAfter(now);
                                }
                                return dayjs.tz(`${date.format('YYYY-MM-DD')} ${time}`, 'YYYY-MM-DD h:mm A', selectedTimezone).isAfter(selectedStartTime);
                            }).map((time, index) => (
                                <MenuItem key={index}
                                          value={time}>{time} {" "} {getDuration(startTime, time)}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <FormControl fullWidth sx={{marginTop: '16px'}}>
                    <InputLabel sx={{color: '#555', fontFamily: 'Inter'}}>Timezone</InputLabel>
                    <Select
                        value={selectedTimezone}
                        label={"Timezone"}
                        onChange={(e) => setSelectedTimezone(e.target.value)}
                        displayEmpty
                        sx={{color: '#000', fontFamily: 'Inter'}}
                    >
                        {timezones.map((tz, index) => (
                            <MenuItem key={index} value={tz.value}>{tz.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Box sx={{display: 'flex', flexDirection: 'row', gap: '12px', alignItems: 'center'}}>
                    <TextField
                        fullWidth
                        label="Invite people via email"
                        variant="outlined"
                        name={'email'}
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        error={formik.touched.email && Boolean(formik.errors.email)}
                        helperText={formik.touched.email && formik.errors.email}
                        margin="normal"
                        InputProps={{style: {color: '#000', height: '52px', fontFamily: 'Inter'}}}
                        InputLabelProps={{style: {color: '#555', fontFamily: 'Inter'}}}
                    />
                    <Box mt={1}>
                        <Button variant="contained" color="primary"
                                sx={{backgroundColor: '#000', color: '#fff', width: '100px', height: '52px'}}
                                onClick={handleAddEmail} disabled={addedEmails.length >= 8}>Add</Button>
                    </Box>
                </Box>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    alignSelf: 'flex-start',
                    width: '100%',
                    marginBottom: '8px'
                }}>
                    {emailError &&
                        <Box sx={{alignSelf: 'flex-start'}}><Typography variant="body1" color="error">Please enter a
                            valid email address.</Typography></Box>}
                    {emailAlreadyInvited && <Box><Typography variant="body1" color="error">You have already
                        added {emailAlreadyInvited}.</Typography></Box>}
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '12px',
                        alignSelf: 'flex-start',
                        width: '100%',
                        maxHeight: '210px',
                        overflowY: 'auto',
                        scrollbarWidth: 'thin',
                        '&::-webkit-scrollbar': {width: '6px'},
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#cccccc',
                            borderRadius: '3px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            backgroundColor: '#aaaaaa',
                        },
                    }}
                >
                    {addedEmails && addedEmails.length > 0 && (
                        <Box
                            sx={{
                                fontSize: {sm: '17px', md: '17px'},
                                fontWeight: '500',
                                color: '#6f6e77',
                                textAlign: 'center',
                                paddingX: {xs: '20px', sm: '0'},
                                width: '100%',
                            }}
                        >
                            An email with instructions on how to join the session will be sent to all invitees.
                        </Box>
                    )}

                    {addedEmails &&
                        addedEmails.length > 0 &&
                        addedEmails.map((email) => (
                            <Box
                                key={email}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: {xs: '100%', sm: 'calc(50% - 6px)'}, // adjust gap offset
                                    padding: '4px 8px',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '6px',
                                }}
                            >
                                <EmailBadge email={email}/>
                                <SessionTrashButton handleClick={() => removeAddedEmail(email)}/>
                            </Box>
                        ))}
                </Box>

                <TextField
                    fullWidth
                    label="Description"
                    variant="outlined"
                    multiline
                    rows={3}
                    value={sessionDescription}
                    onChange={(e) => setSessionDescription(e.target.value)}
                    margin="normal"
                    inputProps={{ maxLength: 500 }}
                    InputProps={{style: {color: '#000'}}}
                    InputLabelProps={{style: {color: '#555'}}}
                />

                <Button variant="contained" color="primary" disabled={sessionName === ''}
                        sx={{backgroundColor: '#000', color: '#fff', fontFamily: 'Inter', width: '100%', mt: 2}}
                        onClick={handleCreateSession}>Create session</Button>
            </Box>
        </LocalizationProvider>
    );
};

export default ScheduleSessionForm;
